import {
  PHONE_REGEX_WITHOUT_HYPHEN,
  COUNTRY_US,
  COUNTRY_CA,
  US_STATES,
  CANADA_PROVINCES,
  ZIP_CODE_US_REGEX,
  ZIP_CODE_CA_REGEX,
  ADDRESS_REGEX,
  EMAIL_REGEX,
  PHONE_REGEX,
  EXT_REGEX,
  CYBERSOURCE_COMPANY_NAME_FIRST_CHAR_REGEX,
  CYBERSOURCE_COMPANY_NAME_REGEX,
  COMPANY_NAME_PHONE_TOKEN_REGEX,
  COMPANY_NAME_SSN_DASHED_REGEX,
  COMPANY_NAME_SSN_DIGITS_ONLY_REGEX,
  CYBERSOURCE_COMPANY_NAME_ISSUE,
} from 'constants/AppConstants';

export const getStateList = (country) => {
  switch (country) {
    case COUNTRY_US:
      return US_STATES.map((state) => ({ id: state.abbreviation, description: state.abbreviation }));
    case COUNTRY_CA:
      return CANADA_PROVINCES.map((state) => ({ id: state.abbreviation, description: state.abbreviation }));
    default:
      return [{ id: '', description: '' }];
  }
};

export const validateZipCode = (country, zipCode) => {
  const zipCodeRegex = country === COUNTRY_US ? ZIP_CODE_US_REGEX : ZIP_CODE_CA_REGEX;
  return zipCodeRegex.test(zipCode);
};

export const validateAddress = (address) => {
  const addressTrimmed = address.trim();
  return ADDRESS_REGEX.test(addressTrimmed);
};

export const validateEmail = (email) => {
  const emailTrimmed = email.trim();
  return EMAIL_REGEX.test(emailTrimmed);
};

export const validateCountry = (country) => country === COUNTRY_US || country === COUNTRY_CA;

export const validatePhoneNumber = (phoneNumber) => {
  const phoneNumberTrimmed = phoneNumber.trim();
  return PHONE_REGEX.test(phoneNumberTrimmed) || PHONE_REGEX_WITHOUT_HYPHEN.test(phoneNumberTrimmed);
};

export const validateText = (value) => value.trim().length > 0;

// #genai An email address never spans whitespace, so each space-delimited token is scanned with
// plain string operations rather than a search regex (which would backtrack over long values).
// A token is email-like when it has text before an "@" and, after it, a dot followed by at least
// two more characters.
const isEmailLikeToken = (token) => {
  const at = token.indexOf('@');
  if (at < 1) return false;
  const domain = token.slice(at + 1);
  const dot = domain.indexOf('.');
  return dot > 0 && domain.length - dot > 2;
};

// #genai Conservative PII: embedded email, US 10-digit phone token, dashed SSN, or a value that is
// exactly 9 digits. Checked before charset because those values can otherwise pass the allowed
// character set.
const companyNameContainsPii = (trimmed) => trimmed.split(' ').some(isEmailLikeToken)
  || COMPANY_NAME_PHONE_TOKEN_REGEX.test(trimmed)
  || COMPANY_NAME_SSN_DASHED_REGEX.test(trimmed)
  || COMPANY_NAME_SSN_DIGITS_ONLY_REGEX.test(trimmed);

export const getCybersourceCompanyNameIssue = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return CYBERSOURCE_COMPANY_NAME_ISSUE.EMPTY;
  if (companyNameContainsPii(trimmed)) return CYBERSOURCE_COMPANY_NAME_ISSUE.PII;
  // #genai First-character failures get their own copy ("must start with..."); remaining
  // unsupported characters use the shorter charset message.
  if (!CYBERSOURCE_COMPANY_NAME_FIRST_CHAR_REGEX.test(trimmed)) {
    return CYBERSOURCE_COMPANY_NAME_ISSUE.FIRST_CHAR;
  }
  if (!CYBERSOURCE_COMPANY_NAME_REGEX.test(trimmed)) return CYBERSOURCE_COMPANY_NAME_ISSUE.CHARSET;
  return CYBERSOURCE_COMPANY_NAME_ISSUE.VALID;
};

export const isValidCybersourceCompanyName = (value) => (
  getCybersourceCompanyNameIssue(value) === CYBERSOURCE_COMPANY_NAME_ISSUE.VALID
);

const COMPANY_NAME_INLINE_KEYS = {
  [CYBERSOURCE_COMPANY_NAME_ISSUE.EMPTY]: 'address_information__field_required',
  [CYBERSOURCE_COMPANY_NAME_ISSUE.PII]: 'address_information__company_name_pii',
  [CYBERSOURCE_COMPANY_NAME_ISSUE.FIRST_CHAR]: 'address_information__company_name_first_char',
  [CYBERSOURCE_COMPANY_NAME_ISSUE.CHARSET]: 'address_information__company_name_unsupported',
};

export const getCybersourceCompanyNameInvalidKey = (value) => (
  COMPANY_NAME_INLINE_KEYS[getCybersourceCompanyNameIssue(value)]
    ?? 'address_information__company_name_unsupported'
);

export const validateState = (country, state) => {
  const states = getStateList(country);
  if (states.length === 0) return false;
  if (states.every((st) => st.id === '')) return false;
  return states.some((validState) => validState.id === state);
};

export const validateExt = (ext) => {
  const extTrimmed = ext.trim();
  return EXT_REGEX.test(extTrimmed);
};

export const checkIfMessagesAreUpdated = ((elementRefInfo, errors) => Object.keys(elementRefInfo).some((key) => {
  if (key in errors) {
    return errors[key] !== elementRefInfo[key].message;
  }
  return false;
}));

export const updateMessages = (elementRefInfo, errors) => {
  const updatedElementRefInfo = { ...elementRefInfo };
  Object.keys(elementRefInfo).forEach((key) => {
    if (key in errors) {
      updatedElementRefInfo[key].message = errors[key];
    }
  });
  return updatedElementRefInfo;
};
